import { useMemo } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { Box, styled } from '@mui/material'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { observer } from 'mobx-react-lite'
import { FONT_FAMILIES } from 'src/theme/typography.theme'

import soupSaladBG from '@assets/visuals/soup_salad_card_bg.png'
import {
  AcBox,
  AcButton,
  AcCard,
  AcColumn,
  AcGridContainer,
  AcGridItem,
  AcIcon,
  AcRow,
  AcTag,
  AcTypography,
} from '@components'
import {
  DIALOGS,
  ICONS,
  WEEKDAYS_KEYS,
  WEEKDAYS_LABELS_SHORT,
} from '@constants'
import { LABELS } from '@constants'
import { formatPrice } from '@helpers'
import { useBreakpoints, useStore } from '@hooks'
import { IWeekDay } from '@typings'

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    maxWidth: 300,
    padding: '0.75rem',
    fontSize: theme.typography.pxToRem(12),
    border: `1px solid #C1D6CC`,
  },
}))

export const AcLunchProfileTab = observer(() => {
  const { user, dialog } = useStore()
  const { isMobile } = useBreakpoints()

  const hasWeekMenu = useMemo(() => {
    return user.hasSoup || user.hasSalad
  }, [user.current])

  const getWeekLunchers = useMemo(() => {
    const values = user.customer?.lunch_days.map(day => Object.values(day)[0])
    if (!values) {
      return 0
    }
    return values.reduce((acc, value) => {
      return acc + value
    })
  }, [user.current])

  const renderTableHeader = useMemo(() => {
    return (
      <AcGridContainer>
        <AcGridItem mobile={7}></AcGridItem>
        {Object.values(WEEKDAYS_LABELS_SHORT).map(label => (
          <AcGridItem
            key={label}
            mobile={1}
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}>
            <AcTypography
              component="span"
              textAlign="center"
              sx={{ color: 'black.main' }}>
              {label}
            </AcTypography>
          </AcGridItem>
        ))}
      </AcGridContainer>
    )
  }, [])

  const renderDeliveryDays = useMemo(() => {
    const getDeliveryDayIcon = (day: IWeekDay) => {
      if (!user.customer?.delivery_days.includes(day)) {
        return <CloseIcon sx={{ color: '#B7C3C1' }} />
      }
      return <AcIcon icon={ICONS.DELIVERY_TRUCK} />
    }

    return (
      <AcGridContainer>
        <AcGridItem
          mobile={12}
          tablet={7}>
          <AcTypography
            component="span"
            bold>
            {LABELS.DELIVERY_DAYS}
          </AcTypography>
        </AcGridItem>
        {Object.values(WEEKDAYS_KEYS).map(label => (
          <AcGridItem
            mobile={2}
            tablet={1}
            key={label}>
            <AcColumn alignItems="center">
              {isMobile && (
                <span>
                  {
                    WEEKDAYS_LABELS_SHORT[
                      label.toUpperCase() as keyof typeof WEEKDAYS_LABELS_SHORT
                    ]
                  }
                </span>
              )}
              <span>{getDeliveryDayIcon(label as IWeekDay)}</span>
            </AcColumn>
          </AcGridItem>
        ))}
      </AcGridContainer>
    )
  }, [user.customer, isMobile])

  const renderLunchDays = useMemo(() => {
    const getLunchDayIcon = (day: IWeekDay) => {
      if (
        !user.customer?.lunch_days.some(lunchDay =>
          lunchDay.hasOwnProperty(day),
        )
      ) {
        return <CloseIcon sx={{ color: '#B7C3C1' }} />
      }
      return <AcIcon icon={ICONS.KNIFE_FORK} />
    }

    return (
      <AcGridContainer>
        <AcGridItem
          mobile={12}
          tablet={7}>
          <AcTypography
            component="span"
            bold>
            {LABELS.LUNCH_DAYS}
          </AcTypography>
        </AcGridItem>
        {Object.values(WEEKDAYS_KEYS).map(label => (
          <AcGridItem
            key={label}
            mobile={2}
            tablet={1}>
            <AcColumn alignItems="center">
              {isMobile && (
                <span>
                  {
                    WEEKDAYS_LABELS_SHORT[
                      label.toUpperCase() as keyof typeof WEEKDAYS_LABELS_SHORT
                    ]
                  }
                </span>
              )}
              <span>{getLunchDayIcon(label as IWeekDay)}</span>
            </AcColumn>
          </AcGridItem>
        ))}
      </AcGridContainer>
    )
  }, [user.customer, isMobile])

  const renderWeekMenuCard = useMemo(() => {
    if (!hasWeekMenu) return null
    return (
      <AcGridItem
        mobile={12}
        tablet={6}>
        <AcCard
          cardBackgroundImage={soupSaladBG}
          cardColor="pink">
          <AcColumn
            gap={2}
            minHeight={146}>
            <AcTypography
              removePadding
              component="h2">
              {LABELS.WEEK_MENU}
            </AcTypography>
            <AcRow gap={2}>
              {user.hasSalad && (
                <AcTag
                  large
                  color="white"
                  label="Salade"
                />
              )}
              {user.hasSoup && (
                <AcTag
                  large
                  color="white"
                  label="Soep"
                />
              )}
            </AcRow>
          </AcColumn>
        </AcCard>
      </AcGridItem>
    )
  }, [user.current])

  const renderLuncherCount = useMemo(() => {
    const countFlat = Object.values(WEEKDAYS_KEYS).map(day => {
      const item = user.customer?.lunch_days.find(lunchDay =>
        lunchDay.hasOwnProperty(day),
      )

      return {
        day,
        count: !item ? 0 : Object.values(item)[0],
      }
    })

    return (
      <AcGridContainer>
        <AcGridItem
          mobile={12}
          tablet={7}>
          <AcTypography
            component="span"
            bold>
            Standaard Lunchers p.d.
          </AcTypography>
        </AcGridItem>

        {countFlat.map((item, index) => {
          return (
            <AcGridItem
              key={index}
              mobile={2}
              tablet={1}>
              <AcColumn alignItems="center">
                {isMobile && (
                  <span>
                    {
                      WEEKDAYS_LABELS_SHORT[
                        item.day.toUpperCase() as keyof typeof WEEKDAYS_LABELS_SHORT
                      ]
                    }
                  </span>
                )}

                <AcTypography
                  component="span"
                  textAlign="center"
                  bold>
                  {item.count}
                </AcTypography>
              </AcColumn>
            </AcGridItem>
          )
        })}
      </AcGridContainer>
    )
  }, [user.customer, isMobile])

  if (!user.current || !user.customer) {
    return null
  }

  return (
    <>
      {user.isBasicSubscription && (
        <AcRow
          pb={4}
          justifyContent="space-between"
          alignItems="start">
          <AcTypography
            removePadding
            size="sm"
            color="green.main">
            Wil je je vaste weekmenu wijzigen? Geef dan gemakkelijk je
            wijzigingen door via de gele button of neem contact op via{' '}
            <a href="tel:0887302200">088-730 2200</a> of{' '}
            <a href="mailto:service@debuurtboer.nl">service@debuurtboer.nl</a>{' '}
            <HtmlTooltip
              placement={isMobile ? 'bottom' : 'right'}
              title={
                <>
                  * Let op, als je je wijziging doorvoert na donderdag 14:00 uur
                  zullen de wijzigingen een week later ingaan. Je krijgt een
                  bevestiging van de wijziging per mail.
                </>
              }>
              <Box
                sx={{
                  display: 'inline-flex',
                  position: 'absolute',
                  cursor: 'pointer',
                  maxHeight: '1rem',
                  svg: {
                    maxHeight: '1rem',
                  },
                }}>
                <AcIcon icon={ICONS.INFO} />
              </Box>
            </HtmlTooltip>
          </AcTypography>
          <AcButton
            style={{
              padding: '0 3rem',
            }}
            onClick={() =>
              dialog.open(DIALOGS.CHANGE_BASIC_SUBSCRIPTION_PROFILE)
            }>
            {LABELS.EDIT_CAPITALIZED}
          </AcButton>
        </AcRow>
      )}
      <AcGridContainer>
        <AcGridItem
          mobile={12}
          tablet={hasWeekMenu ? 6 : 12}>
          <AcCard cardColor="green">
            <AcColumn
              justifyContent="space-between"
              minHeight={146}>
              <AcBox>
                <AcTypography
                  component="h3"
                  padding="sm">
                  Lunchers
                </AcTypography>
                <AcTypography>
                  Aantal afgesproken lunchers per week
                </AcTypography>
              </AcBox>
              <AcRow
                justifyContent="space-between"
                alignItems="flex-end">
                <AcTypography
                  color="yellow.main"
                  lineHeight={0.5}
                  fontSize={64}
                  fontFamily={FONT_FAMILIES.ROCA}>
                  {getWeekLunchers}
                </AcTypography>
                <AcTypography
                  component="span"
                  size="xs">
                  EUR {formatPrice(user.customer?.budget)} Lunchbudget pp
                </AcTypography>
              </AcRow>
            </AcColumn>
          </AcCard>
        </AcGridItem>
        {renderWeekMenuCard}

        <AcGridItem mobile={12}>
          <AcCard contentColor="green.main">
            <AcTypography
              component="h3"
              removePadding>
              Bezorgafspraken
            </AcTypography>
            <AcColumn gap={isMobile ? 4 : 1}>
              {!isMobile && renderTableHeader}
              {renderDeliveryDays}
              {renderLunchDays}
              {renderLuncherCount}
            </AcColumn>
          </AcCard>
        </AcGridItem>
      </AcGridContainer>
    </>
  )
})
